import { type UserFileRead } from '@/uppy-file-upload/schemas'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { TimelineFileStatus } from '../constants'

export type TimelineFileCardState = {
  fileName: string
  status: Exclude<TimelineFileStatus, TimelineFileStatus.EXTRACTED>
  errorMessage: string
  ready: boolean
}

export function isActiveTimelineUserFileUpload(uploadState?: FileUploadState): uploadState is FileUploadState {
  return !!uploadState && ['validating', 'queued', 'initializing', 'uploading'].includes(uploadState.client.uploadStatus)
}

export function getTimelineAssetFileCardState(params: {
  fileId?: string
  file?: UserFileRead
  uploadState?: FileUploadState
  loadError?: boolean
}): TimelineFileCardState {
  const { fileId, file, uploadState, loadError } = params
  const clientStatus = uploadState?.client.uploadStatus
  const record = uploadState?.backend?.record ?? file
  const fileName = record?.original_filename ?? uploadState?.client.filename ?? fileId ?? 'File'

  if (clientStatus === 'unsupported_type') {
    return { fileName, status: TimelineFileStatus.ERROR, errorMessage: 'Unsupported file type', ready: false }
  }

  if (clientStatus === 'error') {
    return { fileName, status: TimelineFileStatus.ERROR, errorMessage: uploadState?.client.uploadError ?? 'Upload failed', ready: false }
  }

  if (loadError && !record) {
    return { fileName, status: TimelineFileStatus.ERROR, errorMessage: 'Could not load file', ready: false }
  }

  if (isActiveTimelineUserFileUpload(uploadState)) {
    return { fileName, status: TimelineFileStatus.UPLOADING, errorMessage: '', ready: false }
  }

  if (!record) {
    return { fileName, status: TimelineFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  if (record.processing_status === UserFileProcessingStatus.Failed) {
    return { fileName, status: TimelineFileStatus.ERROR, errorMessage: record.processing_error ?? 'Processing failed', ready: false }
  }

  if (record.processing_status === UserFileProcessingStatus.Pending || record.processing_status === UserFileProcessingStatus.Processing) {
    return { fileName, status: TimelineFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  return { fileName, status: TimelineFileStatus.COMPLETED, errorMessage: '', ready: true }
}
